import {
	Alert,
	Text,
	Box,
	Stack,
	Spinner,
	Container,
	Heading,
	Component,
	Inline,
	Button,
	Icons,
	Divider,
	Grid,
	Col,
	Table,
	RadioInput,
	Dropdown,
} from "@sembark-travel/ui/base"
import { FeatureFlag, useAuthUser, useHasFeatureFlag } from "../Auth"
import { useTenant } from "./store"
import {
	Form,
	withServerErrors,
	FieldArray,
	SelectInputField,
	useFieldValue,
	SubmissionError,
	FormApi,
	validateFormValues,
} from "@sembark-travel/ui/form"
import { useXHR } from "@sembark-travel/xhr"
import {
	CurrencyExchangeRatesList,
	useAllAvailableCurrencies,
} from "../Currencies"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
} from "@sembark-travel/ui/dialog"
import { useState } from "react"
import arrayMutators from "final-form-arrays"
import { TCurrency } from "@sembark-travel/money"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import * as Validator from "yup"

// Constants
const ROUNDING_OPTIONS = [
	{ value: "1", label: "1" },
	{ value: "5", label: "5" },
	{ value: "10", label: "10" },
	{ value: "50", label: "50" },
	{ value: "100", label: "100" },
] as const

const FORMAT_OPTIONS = [
	{ value: "en_IN", label: "Indian Standard (1,00,00,000.00)" },
	{ value: "en_US", label: "International Standard (10,000,000.00)" },
	{ value: "de_DE", label: "European Dot Separator (10.000.000,00)" },
] as const

const ROUNDING_EXAMPLE = "e.g.- 36,803.20 → Rounded (10) → 36,810"

const getFormatLabel = (format: string): string => {
	const option = FORMAT_OPTIONS.find((opt) => opt.value === format)
	return option?.label || format
}

function CurrencyRow({
	name,
	index,
	allAvailableCurrencies,
	fields,
	isEdit,
	form,
}: {
	name: string
	index: number
	allAvailableCurrencies: Array<{
		id: string
		name: string
		iso: string
		locale: string
		rounding: string | null
	}>
	fields: { length?: number; remove: (index: number) => void }
	isEdit: boolean
	form: FormApi<Record<string, unknown>>
}) {
	const hasMultiCurrency = useHasFeatureFlag("multi_currency")
	const currentCurrency = useFieldValue(`${name}.currency`).value
	const allSelectedCurrencies = useFieldValue("currencySettings")
		.value as Array<{ currency: string }>

	// Filter out already selected currencies from available options
	const availableCurrencies = allAvailableCurrencies.filter((currency) => {
		// Include current currency if it's selected (not null/empty)
		if (currentCurrency && currency.iso === currentCurrency) return true
		// Exclude other selected currencies
		return !allSelectedCurrencies?.some(
			(item) => item.currency === currency.iso
		)
	})

	const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const newCurrency = e.target.value
		form.change(`${name}.currency`, newCurrency)
		form.change(
			`${name}.rounding`,
			availableCurrencies.find((c) => c.iso === newCurrency)?.rounding || "1"
		)
		form.change(
			`${name}.format`,
			availableCurrencies.find((c) => c.iso === newCurrency)?.locale || "en_IN"
		)
	}

	return (
		<Box>
			<Grid gap="6">
				<Col>
					<SelectInputField
						name={`${name}.currency`}
						label="Currencies"
						placeholder="Select Currencies"
						required
						onChange={handleCurrencyChange}
						disabled={!hasMultiCurrency}
					>
						<option value="">Select Currency</option>
						{availableCurrencies.map((currency) => (
							<option key={currency.iso} value={currency.iso}>
								{currency.name}
							</option>
						))}
					</SelectInputField>
				</Col>
				<Col>
					<SelectInputField
						name={`${name}.rounding`}
						label="Quotation Rounding"
						placeholder="Selected Rounding"
					>
						{ROUNDING_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</SelectInputField>
					{index === 0 && (
						<Text color="muted" fontSize="xs" style={{ marginTop: "4px" }}>
							{ROUNDING_EXAMPLE}
						</Text>
					)}
				</Col>
				<Col>
					<Inline gap="2" alignItems="end">
						<Box flex="1">
							<SelectInputField
								name={`${name}.format`}
								label="Format"
								placeholder="Select Format"
							>
								{FORMAT_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</SelectInputField>
						</Box>
						{!isEdit && Number(fields.length || 0) > 1 && (
							<Button
								type="button"
								onClick={() => fields.remove(index)}
								level="tertiary"
								size="sm"
								title="Remove Currency"
							>
								<Icons.Cancel />
							</Button>
						)}
					</Inline>
				</Col>
			</Grid>
		</Box>
	)
}

export function Currencies() {
	const { user } = useAuthUser()
	const { tenant, isFetching, error, refresh } = useTenant(user?.tenant?.id)
	const xhr = useXHR()
	if (!user || !user.tenant) {
		return null
	}
	if (isFetching) {
		return <Spinner padding="4" alignCenter />
	}
	if (!tenant) {
		if (error) {
			return <Alert status="error">{error.message}</Alert>
		}
		return null
	}
	const { solidus: apiSolidus, all_currencies } = tenant

	return (
		<Stack gap="4">
			<Box borderBottomWidth="1" bgColor="default">
				<Container paddingY="4">
					<Heading as="h3">Currencies</Heading>
				</Container>
			</Box>
			<Stack>
				<Inline justifyContent="between" paddingX="4">
					<Text fontWeight="semibold" fontSize="lg">
						Currencies and Rounding
					</Text>
					<FeatureFlag flag="multi_currency">
						<Component initialState={false}>
							{({ state: open, setState: setOpen }) => {
								return (
									<>
										<Button status="primary" onClick={() => setOpen(true)}>
											<Icons.Plus />
											Add Currencies
										</Button>
										<AddCurrencyDialog
											open={open}
											tenant_id={tenant.id}
											onClose={() => setOpen(false)}
											currencies={all_currencies}
											refresh={refresh}
										/>
									</>
								)
							}}
						</Component>
					</FeatureFlag>
				</Inline>
				<Divider sm marginY="3" />
				<Stack paddingX="4" gap="4">
					<Inline gap="2" alignItems="center">
						<Text color="muted">Solidus (/-):</Text>
						<Text fontWeight="semibold">
							{apiSolidus ? "Enabled" : "Disabled"}
						</Text>
						<Component initialState={false}>
							{({ state: editOpen, setState: setEditOpen }) => (
								<>
									<Icons.Pencil
										cursor="pointer"
										onClick={() => setEditOpen(true)}
									/>
									<EditSolidusDialog
										open={editOpen}
										onClose={() => setEditOpen(false)}
										tenant_id={tenant.id}
										initialSolidus={Boolean(apiSolidus)}
										refresh={refresh}
									/>
								</>
							)}
						</Component>
					</Inline>
					<Box overflowY="auto" rounded="md">
						<Table
							bordered
							hover
							responsive
							alignCols={{ 1: "center" }}
							headers={["Currencies", "Quotation Rounding", "Format", ""]}
							rows={all_currencies.map((c) => [
								c.currency,
								<Text>{c.rounding || "1"}</Text>,
								<Text>{getFormatLabel(c.format)}</Text>,
								<Dropdown alignRight>
									<Dropdown.Toggle paddingX="4">
										<Icons.DotsVertical />
									</Dropdown.Toggle>
									<Dropdown.Menu>
										<Component initialState={false}>
											{({ state: editOpen, setState: setEditOpen }) => (
												<>
													<Dropdown.MenuItem onClick={() => setEditOpen(true)}>
														<Icons.Pencil color="muted" />
														<Box display="inlineBlock" marginLeft="2">
															Edit
														</Box>
													</Dropdown.MenuItem>
													<AddCurrencyDialog
														open={editOpen}
														onClose={() => setEditOpen(false)}
														tenant_id={tenant.id}
														isEdit={true}
														currencies={all_currencies}
														editingCurrency={{ ...c, id: c.id }}
														refresh={refresh}
													/>
												</>
											)}
										</Component>
										<FeatureFlag flag="multi_currency">
											<ConfirmationDialogProvider>
												{({ confirm }) => (
													<>
														<Dropdown.MenuItem
															onClick={() => {
																confirm().then(async (yes) => {
																	if (!yes) return
																	try {
																		const resp = await xhr.delete(
																			`/tenants/${tenant.id}/currencies/${c.id}`
																		)
																		showSnackbar(resp.data.message)
																		refresh()
																	} catch (e) {
																		showSnackbar(
																			(e as Error).message ||
																				"Something went wrong. Please try later."
																		)
																	}
																})
															}}
														>
															<Icons.Trash color="danger" />
															<Box display="inlineBlock" marginLeft="2">
																Delete
															</Box>
														</Dropdown.MenuItem>
														<ConfirmationDialog
															title="Delete Currency"
															destructiveAction
														>
															<Stack gap="4">
																<Stack gap="2">
																	<Text color="danger" fontWeight="semibold">
																		<Icons.Attention /> Once deleted, Currency
																		can not be restored.
																	</Text>
																	<Text fontSize="sm" color="muted">
																		You will be able to add new one.
																	</Text>
																</Stack>
																<Text>
																	{`Are you sure you want to delete "${c.currency}" currency ?`}
																	<br />
																	<Text color="muted" fontSize="sm">
																		If any destination is using this currency,
																		it will be changed to the functional
																		currency.
																	</Text>
																</Text>
															</Stack>
														</ConfirmationDialog>
													</>
												)}
											</ConfirmationDialogProvider>
										</FeatureFlag>
									</Dropdown.Menu>
								</Dropdown>,
							])}
						/>
					</Box>
				</Stack>
			</Stack>
			<FeatureFlag flag="multi_currency">
				{all_currencies.length > 1 ? <CurrencyExchangeRatesList /> : null}
			</FeatureFlag>
		</Stack>
	)
}

const currencySettingsValidationSchema = Validator.object().shape({
	currencySettings: Validator.array().of(
		Validator.object().shape({
			currency: Validator.string().required("Currency is required").nullable(),
			rounding: Validator.string().nullable(),
			format: Validator.string(),
		})
	),
})

function AddCurrencyDialog({
	open,
	onClose,
	currencies,
	title,
	tenant_id,
	isEdit = false,
	editingCurrency,
	refresh,
}: {
	open: boolean
	onClose: () => void
	currencies: Array<TCurrency>
	title?: string
	tenant_id: number
	isEdit?: boolean
	editingCurrency?: TCurrency & { id?: number }
	refresh?: () => void
}) {
	const { data: allAvailableCurrencies = [] } = useAllAvailableCurrencies()
	const xhr = useXHR()

	return (
		<Dialog
			open={open}
			onClose={onClose}
			title={title || "Currencies and Rounding"}
			lg
		>
			<Dialog.Body>
				<Stack gap="2">
					<Form
						initialValues={{
							currencySettings: isEdit
								? [
										{
											currency: editingCurrency?.currency,
											rounding: editingCurrency?.rounding || "1",
											format: editingCurrency?.format || "en_IN",
										},
									]
								: currencies.map((currency) => ({
										currency: currency.currency,
										rounding: currency.rounding || "1",
										format: currency.format || "en_IN",
									})),
						}}
						validate={validateFormValues(currencySettingsValidationSchema)}
						onSubmit={withServerErrors(async (values) => {
							const method = isEdit ? "put" : "post"

							if (isEdit && editingCurrency?.id) {
								// In edit mode: send a single object
								const updatedCurrency = {
									...values.currencySettings[0], // you already treat index 0 as the edited item
								}

								const resp = await xhr[method](
									`/tenants/${tenant_id}/currencies/${editingCurrency.id}`,
									updatedCurrency
								)
								showSnackbar(resp.data.message)
							} else {
								// In create mode: send array of currencies
								await xhr[method](`/tenants/${tenant_id}/currencies`, {
									currencies: values.currencySettings,
								})
							}

							refresh?.()
							onClose()
						})}
						mutators={{ ...arrayMutators }}
					>
						{({ handleSubmit, form, submitting }) => (
							<form noValidate onSubmit={handleSubmit}>
								<FieldArray name="currencySettings">
									{({ fields }) => (
										<Stack gap="4" paddingBottom="4">
											<Stack gap="4" paddingRight="4">
												{fields.map((name, index) => (
													<CurrencyRow
														key={name}
														name={name}
														index={index}
														allAvailableCurrencies={allAvailableCurrencies}
														fields={fields}
														isEdit={isEdit}
														form={form}
													/>
												))}
											</Stack>
											{!isEdit && (
												<Box>
													<Button
														title="Add More Currencies"
														type="button"
														onClick={() =>
															fields.push({
																currency: null,
																rounding: "1",
																format: "en_IN",
															})
														}
														status="primary"
													>
														<Icons.Plus /> Add More
													</Button>
												</Box>
											)}
										</Stack>
									)}
								</FieldArray>
								<SubmissionError />
								<Box>
									<Inline
										gap="4"
										justifyContent="end"
										borderTopWidth="1"
										borderColor="default"
										paddingTop="4"
									>
										<Button onClick={onClose} level="tertiary">
											Cancel
										</Button>
										<Button type="submit" disabled={submitting}>
											{submitting ? (
												<>
													<Icons.Spinner animation="spin" /> Saving...
												</>
											) : (
												"Save"
											)}
										</Button>
									</Inline>
								</Box>
							</form>
						)}
					</Form>
				</Stack>
			</Dialog.Body>
		</Dialog>
	)
}

// Dialog to edit solidus
function EditSolidusDialog({
	open,
	onClose,
	tenant_id,
	initialSolidus,
	refresh,
}: {
	open: boolean
	onClose: () => void
	tenant_id: number
	initialSolidus: boolean
	refresh: () => void
}) {
	const [solidus, setSolidus] = useState(initialSolidus)
	const [isSaving, setIsSaving] = useState(false)
	const xhr = useXHR()

	const handleClose = () => {
		setSolidus(initialSolidus)
		onClose()
	}

	const handleSaveSolidus = async () => {
		setIsSaving(true)
		try {
			await xhr.post(`/tenants/${tenant_id}/solidus`, {
				solidus: solidus ? 1 : 0,
			})
			refresh()
			showSnackbar("Solidus setting updated successfully")
			onClose()
		} catch (error) {
			showSnackbar("Failed to update solidus setting")
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<Dialog open={open} onClose={handleClose} title="Solidus">
			<Dialog.Body>
				<Stack gap="6">
					<Text color="muted">
						Choose whether to enable or disable the solidus (/-) separator in
						currency formatting.
					</Text>
					<Grid gap="4">
						<Col>
							<Box
								padding="4"
								borderWidth="1"
								rounded="md"
								cursor="pointer"
								onClick={() => setSolidus(true)}
								bgColor={solidus ? "accent" : "default"}
								borderColor={solidus ? "primary" : "default"}
							>
								<Inline gap="3" alignItems="start">
									<RadioInput
										checked={solidus}
										onChange={() => setSolidus(true)}
									/>
									<Stack gap="2">
										<Text fontWeight="semibold">Enabled</Text>
										<Text fontSize="sm" color="muted">
											Apply Solidus to all price displays in the quotation and
											in all during sharing. e.g USD 345 /-
										</Text>
									</Stack>
								</Inline>
							</Box>
						</Col>
						<Col>
							<Box
								padding="4"
								borderWidth="1"
								rounded="md"
								cursor="pointer"
								onClick={() => setSolidus(false)}
								bgColor={!solidus ? "accent" : "default"}
								borderColor={!solidus ? "primary" : "default"}
							>
								<Inline gap="3" alignItems="start">
									<RadioInput
										checked={!solidus}
										onChange={() => setSolidus(false)}
									/>
									<Stack gap="2">
										<Text fontWeight="semibold">Disabled</Text>
										<Text fontSize="sm" color="muted">
											Prices will appear without solidus in quotations and in
											all sharing. e.g USD 345
										</Text>
									</Stack>
								</Inline>
							</Box>
						</Col>
					</Grid>
				</Stack>
			</Dialog.Body>
			<Dialog.Footer>
				<Inline gap="3" justifyContent="end">
					<Button onClick={handleClose} level="tertiary" disabled={isSaving}>
						Cancel
					</Button>
					<Button
						level="primary"
						disabled={isSaving}
						onClick={handleSaveSolidus}
					>
						{isSaving ? "Saving..." : "Save"}
					</Button>
				</Inline>
			</Dialog.Footer>
		</Dialog>
	)
}
